// Pure presentation math for the question-details page. No Vue/Firebase —
// the component consumes it through webpack; tests run it under plain `node`
// (ESM — the sibling package.json marks this directory `type: module` so node
// can import it without a bundler).
// Test: node tests/node/question-details-format.test.js

export function sum(arr) {
  if (!arr || !arr.length) return 0
  let total = 0
  for (let i = 0; i < arr.length; i++) total += Number(arr[i]) || 0
  return total
}

export function pct(count, total) {
  if (!total) return 0
  return Math.round(((Number(count) || 0) * 100) / total)
}

// datamultichoices = [overallRow, viewerTeamRow (nested children)].
// Depth drives indentation; overall and the viewer's team both sit at depth 0.
// skipViewerRow drops the viewer row itself when the viewer IS the company
// (both rows carry identical data) while keeping its children at depth 1.
export function flattenRows(list, skipViewerRow) {
  const out = []
  if (!list || !list.length) return out
  const walk = (row, depth) => {
    if (!row) return
    out.push({ row, depth })
    const children = row.children || []
    for (let i = 0; i < children.length; i++) walk(children[i], depth + 1)
  }
  if (list[0]) out.push({ row: list[0], depth: 0 })
  if (list[1]) {
    if (skipViewerRow) {
      const children = list[1].children || []
      for (let i = 0; i < children.length; i++) walk(children[i], 1)
    } else {
      walk(list[1], 0)
    }
  }
  return out
}

export function rowCount(row) {
  if (!row) return 0
  if (row.range && row.range.length) return sum(row.range)
  if (row.value && row.value.length) return sum(row.value)
  return 0
}

// Scale: team avg minus company avg, 1 decimal; null when a side is missing.
export function avgDelta(rowAvg, overallAvg) {
  if (typeof rowAvg !== 'number' || typeof overallAvg !== 'number') return null
  return Math.round((rowAvg - overallAvg) * 10) / 10
}

// Yes/No: the store's `yes` flag marks whether index 0 is the positive answer.
export function posIndex(yesFlag) {
  return yesFlag ? 0 : 1
}

export function yesNoShares(value, yesFlag) {
  const total = sum(value)
  const pos = posIndex(yesFlag)
  return {
    posPct: pct(value ? value[pos] : 0, total),
    negPct: pct(value ? value[1 - pos] : 0, total),
    n: total,
  }
}

// Yes/No delta in whole percentage points; null when a side is missing.
export function ptsDelta(rowPct, overallPct) {
  if (typeof rowPct !== 'number' || typeof overallPct !== 'number') return null
  return Math.round(rowPct - overallPct)
}

// Multiple choice: value[] is ascending weight (most negative first) while
// answerOpt[] is descending (most positive first) — option i pairs with
// answerOpt[N-1-i]. See spec "Index subtlety".
export function mcLabelFor(index, answerOpt) {
  if (!answerOpt || !answerOpt.length) return ''
  return answerOpt[answerOpt.length - 1 - index] || ''
}

// Multiple-choice score, computed exactly as the daily report emails do
// (happily-schedule-nest insights.service.ts): each response contributes its
// option's weight from m_choice.weight; score = points / (n × max weight).
// `weights` must already be aligned to value[]'s order — see alignedMcWeights.
// null when there is no data or no usable weights.
export function mcWeightedPct(value, weights) {
  const total = sum(value)
  if (!total || !weights || !weights.length) return null
  const max = Math.max(...weights)
  if (!(max > 0)) return null
  let points = 0
  for (let i = 0; i < value.length; i++) {
    points += (Number(value[i]) || 0) * (Number(weights[i]) || 0)
  }
  return Math.round((points * 100) / (total * max))
}

// Align m_choice.weight with the store's value[] ordering: the store
// (getDataMultiChoicesOther) reverses value[] unless weight[0] === '0' —
// mirror that condition verbatim (string compare included) so any question
// the store reverses gets its weights reversed identically.
export function alignedMcWeights(rawWeights) {
  if (!rawWeights || !rawWeights.length) return []
  const w = rawWeights[0] === '0' ? [...rawWeights] : [...rawWeights].reverse()
  return w.map(Number)
}

// Color slots per option, derived from weight credit so bars can't contradict
// the score (a zero-weight option never renders green — e.g. "Very challenging"
// on the balance-of-challenge question). Option order is preserved; within a
// credit family, shades progress in order of appearance. Callers should fall
// back to mcColorOrder when there are no usable weights.
// Slot map: 1 deep red · 2 red · 8 light red · 3 yellow · 7 orange ·
//           4 green · 5 teal · 6 deep teal.
export function mcCreditColorSlots(weights) {
  const w = (weights || []).map(Number)
  const max = Math.max(...w)
  if (!(max > 0)) return []
  const FAMILY = {
    zero: [1, 2, 8],
    partial: [3, 7],
    full: [4, 5, 6],
  }
  const fams = w.map(x => (x >= max ? 'full' : x <= 0 ? 'zero' : 'partial'))
  const zeroCount = fams.filter(f => f === 'zero').length
  // A lone zero-weight option uses the standard red, not the deep shade.
  const next = { zero: zeroCount === 1 ? 1 : 0, partial: 0, full: 0 }
  return fams.map(f => {
    const list = FAMILY[f]
    const slot = list[Math.min(next[f], list.length - 1)]
    next[f]++
    return slot
  })
}

// Bar color slot order per option count — preserved from the legacy component.
export function mcColorOrder(n) {
  if (n === 2) return [2, 4]
  if (n === 3) return [2, 3, 4]
  if (n === 4) return [2, 3, 4, 5]
  const out = []
  for (let i = 1; i <= n; i++) out.push(i)
  return out
}

export const SCALE_COLOR_ORDER = [2, 3, 4]

